/**
 * Tracking parameter persistence utility.
 *
 * Captures gclid and utm_* parameters from the landing URL and stores them
 * in sessionStorage for the duration of the browser session. A companion
 * React component (TrackingParamsPersister) re-appends these parameters to
 * the address bar on every client-side navigation so that GA4 (with
 * url_passthrough: true) and Meta Pixel can attribute the session correctly
 * across all page views.
 *
 * Why sessionStorage (not cookies)?
 *   - No GDPR consent required: sessionStorage is first-party session storage,
 *     not a tracking cookie. It never leaves the browser.
 *   - Auto-clears when the tab closes, preventing stale gclid values.
 *   - The params remain in the URL (via history.replaceState), so GA4
 *     auto-collection works without any cookie dependency.
 */

const STORAGE_KEY = 'gl_tracking_params';

/** Parameter prefixes that should be persisted across the session. */
const TRACKING_PREFIXES = ['gclid', 'utm_'] as const;

/** A simple key-value map of tracking parameters. */
export type ParamMap = Record<string, string>;

/**
 * Returns true if a parameter name is a tracking parameter (gclid or utm_*).
 */
export function isTrackingParam(key: string): boolean {
  return TRACKING_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Extracts tracking parameters (gclid, utm_*) from a URLSearchParams object.
 */
export function extractTrackingParams(searchParams: URLSearchParams): ParamMap {
  const result: ParamMap = {};
  for (const [key, value] of searchParams) {
    if (isTrackingParam(key)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Reads the tracking parameters currently stored in sessionStorage.
 * Returns an empty object if nothing is stored or if sessionStorage is
 * unavailable (private mode, quota exceeded, etc.).
 */
export function getStoredTrackingParams(): ParamMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as ParamMap;
  } catch {
    return {};
  }
}

/**
 * Captures tracking parameters from the current page URL and stores them
 * in sessionStorage. If tracking params already exist in storage, new
 * values from the URL take precedence (last-click wins).
 *
 * Safe to call multiple times — only updates storage when new tracking
 * params are present in the URL.
 *
 * @returns The merged tracking params (stored + newly captured).
 */
export function captureTrackingParams(): ParamMap {
  const fromUrl = extractTrackingParams(
    new URLSearchParams(window.location.search),
  );
  const urlKeys = Object.keys(fromUrl);

  if (urlKeys.length === 0) return getStoredTrackingParams();

  const stored = getStoredTrackingParams();
  const merged: ParamMap = { ...stored, ...fromUrl };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // sessionStorage might be unavailable (private mode, quota exceeded) —
    // fail silently; the params are still in the URL for the current page.
  }

  return merged;
}

/**
 * Checks whether stored tracking params are missing from the given search
 * string, and if so, returns a new search string with them merged in.
 * Existing non-tracking params are preserved; stored tracking params are
 * only added if not already present.
 *
 * @param pathname - The current pathname (e.g. "/blog").
 * @param search   - The current query string from React Router (e.g. "?tab=1").
 * @returns The full URL with tracking params merged, or null if no changes
 *          were needed (all tracking params already present or none stored).
 */
export function buildUrlWithTrackingParams(
  pathname: string,
  search: string,
): string | null {
  const stored = getStoredTrackingParams();
  const storedKeys = Object.keys(stored);

  if (storedKeys.length === 0) return null;

  const currentParams = new URLSearchParams(search);
  let changed = false;

  for (const key of storedKeys) {
    if (!currentParams.has(key)) {
      currentParams.set(key, stored[key]);
      changed = true;
    }
  }

  if (!changed) return null;

  const newSearch = currentParams.toString();
  return newSearch ? `${pathname}?${newSearch}` : pathname;
}

/**
 * Clears all stored tracking parameters. Primarily for testing.
 */
export function clearTrackingParams(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
